import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogTitleProps,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { COLORS } from '@/styles/tokens/colors';

import { Dialog } from '../Dialog';

export interface DialogModalProps {
  isOpen: boolean;
  heading: React.ReactNode;
  subheading?: React.ReactNode;
  actions?: React.ReactNode;
  onClose: () => void;
  dialogContentSx?: SxProps<Theme>;
  dialogPaperSx?: SxProps<Theme>;
  dialogHeadingContainerSx?: SxProps<Theme>;
  dialogActionsSx?: SxProps<Theme>;
  dialogTitleProps?: DialogTitleProps;
  spacingPxNumber?: number;
  showCloseButton?: boolean;
  /**
   * If true, clicking outside the modal (backdrop click) will not close the modal.
   * Users must explicitly click a button or the X to close.
   */
  disableBackdropClick?: boolean;
}

export const DIALOG_MODAL_MAX_WIDTH = 800 as const;

export function DialogModal({
  isOpen,
  onClose,
  heading,
  subheading,
  actions,
  children,
  dialogContentSx,
  dialogPaperSx,
  dialogHeadingContainerSx,
  dialogActionsSx,
  dialogTitleProps,
  spacingPxNumber = 16,
  showCloseButton = true,
  disableBackdropClick,
}: React.PropsWithChildren<DialogModalProps>) {
  const spacingPx = `${spacingPxNumber}px`;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      disableBackdropClick={disableBackdropClick}
      PaperProps={{
        sx: {
          maxWidth: DIALOG_MODAL_MAX_WIDTH,
          overflowY: 'visible',
          ...dialogPaperSx,
        },
      }}
    >
      <Stack
        direction="row"
        component="header"
        alignItems={subheading ? 'flex-start' : 'center'}
        justifyContent="space-between"
      >
        <Stack
          flex={1}
          minWidth={0}
          p={spacingPx}
          sx={{
            ...dialogHeadingContainerSx,
          }}
        >
          <DialogTitle sx={{ p: 0 }} variant="title-l" {...dialogTitleProps}>
            {heading}
          </DialogTitle>
          {subheading && (
            <Typography
              variant="body-m"
              component="h2"
              sx={{ color: COLORS.TEXT.SECONDARY }}
            >
              {subheading}
            </Typography>
          )}
        </Stack>
        {showCloseButton && (
          <Box
            flexShrink={0}
            pt={spacingPx}
            pr={spacingPx}
            pb={subheading ? 0 : spacingPx}
          >
            <CloseButton
              size="sm"
              variant="transparent"
              aria-label="Close"
              onClick={onClose}
            />
          </Box>
        )}
      </Stack>

      <DialogContent
        sx={{
          padding: `0px ${spacingPx} ${spacingPx} ${spacingPx}`,
          ...dialogContentSx,
        }}
      >
        {children}
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            p: spacingPx,
            ...dialogActionsSx,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}
